import { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FileSearch, 
  Hammer, 
  Camera, 
  Calculator, 
  Brain,
  Wrench,
  FolderPlus,
  MessageSquare,
  CheckCircle,
  Search
} from 'lucide-react';

// =====================================================
// CONFIGURATION DES ANIMATIONS PAR TYPE
// =====================================================
const LOADING_CONFIGS = {
  analyse_devis: {
    icon: FileSearch,
    title: "Léon analyse votre devis",
    messages: [
      "Vérification des postes travaux",
      "Recherche d'oublis fréquents",
      "Analyse des prix du marché",
      "Détection d'incohérences",
      "Vérification des mentions légales",
      "Calcul du score de fiabilité"
    ],
    animationType: "reading",
    color: "amber"
  },
  recette_travaux: {
    icon: Hammer,
    title: "Léon prépare votre recette travaux",
    messages: [
      "Structuration des étapes",
      "Sélection du matériel nécessaire",
      "Vérification des bonnes pratiques",
      "Préparation du guide pas à pas",
      "Calcul des quantités",
      "Ajout des conseils de sécurité"
    ],
    animationType: "working",
    color: "emerald"
  },
  analyse_photo: {
    icon: Camera,
    title: "Léon inspecte votre chantier",
    messages: [
      "Analyse visuelle en cours",
      "Détection des anomalies",
      "Vérification de l'avancement",
      "Préparation des recommandations",
      "Comparaison avec les standards",
      "Évaluation de la qualité"
    ],
    animationType: "inspecting",
    color: "blue"
  },
  budget: {
    icon: Calculator,
    title: "Léon estime votre budget travaux",
    messages: [
      "Analyse des surfaces",
      "Estimation des coûts matériaux",
      "Calcul de la main d'œuvre",
      "Application des coefficients",
      "Vérification des prix du marché",
      "Ajustement du budget global"
    ],
    animationType: "calculating",
    color: "violet"
  },
  reflexion: {
    icon: Brain,
    title: "Léon réfléchit à la meilleure solution",
    messages: [
      "Analyse de votre situation",
      "Recherche de solutions adaptées",
      "Vérification des normes DTU",
      "Consultation de la base de données",
      "Préparation de la réponse",
      "Synthèse des recommandations"
    ],
    animationType: "thinking",
    color: "gold"
  },
  creation_projet: {
    icon: FolderPlus,
    title: "Léon prépare votre projet",
    messages: [
      "Configuration du projet",
      "Génération de la timeline",
      "Préparation des étapes",
      "Calcul des estimations",
      "Organisation des tâches"
    ],
    animationType: "organizing",
    color: "cyan"
  },
  chat: {
    icon: MessageSquare,
    title: "Léon prépare sa réponse",
    messages: [
      "Analyse de votre question",
      "Recherche d'informations",
      "Vérification des données",
      "Rédaction de la réponse"
    ],
    animationType: "thinking",
    color: "gold"
  },
  verification_entreprise: {
    icon: Search,
    title: "Léon vérifie l'entreprise",
    messages: [
      "Consultation du registre SIRENE",
      "Vérification du SIRET",
      "Analyse de l'activité",
      "Calcul du score de confiance"
    ],
    animationType: "inspecting",
    color: "blue"
  },
  generic: {
    icon: Wrench,
    title: "Léon travaille pour vous",
    messages: [
      "Traitement en cours",
      "Analyse des données",
      "Préparation du résultat"
    ],
    animationType: "working",
    color: "gold"
  }
};

// =====================================================
// LÉON AVATAR ANIMÉ (VERSION AMÉLIORÉE)
// =====================================================
function LeonAnimatedAvatar({ animationType = "reading", isBlinking = false, className = "w-40 h-40" }) {
  // Animations spécifiques selon le contexte
  const getAnimationStyle = () => {
    switch (animationType) {
      case "reading":
        return { rotate: [-2, 2, -2] };
      case "working":
        return { rotate: [-5, 5, -5], y: [0, -5, 0] };
      case "inspecting":
        return { scale: [1, 1.05, 1] };
      case "calculating":
        return { x: [-3, 3, -3] };
      case "thinking":
        return { rotate: [0, -3, 0, 3, 0] };
      case "organizing":
        return { y: [0, -8, 0] };
      default:
        return { y: [0, -5, 0] };
    }
  };

  return (
    <motion.div
      animate={getAnimationStyle()}
      transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
    >
      <svg 
        viewBox="0 0 100 100" 
        className={className}
        xmlns="http://www.w3.org/2000/svg"
      >
        {/* Background circle with glow */}
        <defs>
          <filter id="leonGlow">
            <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
          <linearGradient id="hatGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#D4AF37"/>
            <stop offset="100%" stopColor="#B38728"/>
          </linearGradient>
        </defs>
        
        <circle cx="50" cy="50" r="48" fill="#18181b" stroke="#D4AF37" strokeWidth="2" filter="url(#leonGlow)"/>
        
        {/* Face - warm skin tone */}
        <ellipse cx="50" cy="55" rx="28" ry="30" fill="#F5D0A9"/>
        
        {/* Hard hat - Gold/Yellow avec gradient */}
        <ellipse cx="50" cy="28" rx="32" ry="18" fill="url(#hatGradient)"/>
        <rect x="18" y="25" width="64" height="12" rx="2" fill="url(#hatGradient)"/>
        <rect x="22" y="35" width="56" height="4" rx="1" fill="#B38728"/>
        
        {/* Hard hat brim */}
        <ellipse cx="50" cy="38" rx="35" ry="6" fill="#B38728"/>
        
        {/* Hair peeking out */}
        <path d="M 25 42 Q 30 48 35 44" stroke="#5D4037" strokeWidth="3" fill="none" strokeLinecap="round"/>
        <path d="M 75 42 Q 70 48 65 44" stroke="#5D4037" strokeWidth="3" fill="none" strokeLinecap="round"/>
        
        {/* Eyes - animated blink */}
        {isBlinking ? (
          <>
            <path d="M 30 55 Q 38 58 46 55" stroke="#2D1B00" strokeWidth="2.5" fill="none" strokeLinecap="round"/>
            <path d="M 54 55 Q 62 58 70 55" stroke="#2D1B00" strokeWidth="2.5" fill="none" strokeLinecap="round"/>
          </>
        ) : (
          <>
            <ellipse cx="38" cy="55" rx="8" ry="9" fill="white"/>
            <ellipse cx="62" cy="55" rx="8" ry="9" fill="white"/>
            <circle cx="40" cy="56" r="5" fill="#2D1B00"/>
            <circle cx="64" cy="56" r="5" fill="#2D1B00"/>
            <circle cx="42" cy="54" r="2" fill="white"/>
            <circle cx="66" cy="54" r="2" fill="white"/>
          </>
        )}
        
        {/* Eyebrows - expression selon le type */}
        <path d="M 30 46 Q 38 43 46 46" stroke="#5D4037" strokeWidth="2.5" fill="none" strokeLinecap="round"/>
        <path d="M 54 46 Q 62 43 70 46" stroke="#5D4037" strokeWidth="2.5" fill="none" strokeLinecap="round"/>
        
        {/* Nose */}
        <ellipse cx="50" cy="65" rx="3" ry="2" fill="#E8B896"/>
        
        {/* Cheeks - rosy */}
        <circle cx="30" cy="65" r="5" fill="#FFB6B0" opacity="0.5"/>
        <circle cx="70" cy="65" r="5" fill="#FFB6B0" opacity="0.5"/>
        
        {/* Smile - expression selon l'animation */}
        <path 
          d={animationType === "thinking" 
            ? "M 40 74 Q 50 78 60 74" 
            : "M 38 74 Q 50 84 62 74"
          } 
          stroke="#8B4513" 
          strokeWidth="3" 
          fill="none" 
          strokeLinecap="round"
        />
        
        {/* Safety vest collar */}
        <path d="M 25 88 L 35 82 L 50 85 L 65 82 L 75 88" fill="#FF9800" stroke="#E65100" strokeWidth="1"/>
        <path d="M 30 90 L 40 86 L 50 88 L 60 86 L 70 90" fill="#D4AF37" stroke="#B38728" strokeWidth="1"/>
        
        {/* Reflective stripes */}
        <line x1="35" y1="85" x2="40" y2="90" stroke="#FFEB3B" strokeWidth="2"/>
        <line x1="65" y1="85" x2="60" y2="90" stroke="#FFEB3B" strokeWidth="2"/>
      </svg>
    </motion.div>
  );
}

// =====================================================
// COMPOSANT DE CHARGEMENT PRINCIPAL
// =====================================================
function LeonLoadingOverlay({ type = "generic", onCancel, elapsedTime = 0 }) {
  const [isBlinking, setIsBlinking] = useState(false);
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  
  const config = LOADING_CONFIGS[type] || LOADING_CONFIGS.generic;
  const Icon = config.icon;

  // Blinking animation
  useEffect(() => {
    const blinkInterval = setInterval(() => {
      setIsBlinking(true);
      setTimeout(() => setIsBlinking(false), 150);
    }, 3000 + Math.random() * 1000);
    return () => clearInterval(blinkInterval);
  }, []);

  // Rotate messages
  useEffect(() => {
    const messageInterval = setInterval(() => {
      setCurrentMessageIndex(prev => (prev + 1) % config.messages.length);
    }, 2500);
    return () => clearInterval(messageInterval);
  }, [config.messages.length]);

  // Progress animation
  useEffect(() => {
    const progressInterval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 95) return prev;
        return prev + Math.random() * 15;
      });
    }, 800);
    return () => clearInterval(progressInterval);
  }, []);

  // Format elapsed time
  const formatElapsedTime = (ms) => {
    const seconds = Math.floor(ms / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  // Color classes based on config
  const getColorClasses = () => {
    const colors = {
      amber: {
        gradient: "from-amber-500 to-yellow-400",
        text: "text-amber-400",
        bg: "bg-amber-500/20",
        border: "border-amber-500/30",
        glow: "rgba(245, 158, 11, 0.3)"
      },
      emerald: {
        gradient: "from-emerald-500 to-green-400",
        text: "text-emerald-400",
        bg: "bg-emerald-500/20",
        border: "border-emerald-500/30",
        glow: "rgba(16, 185, 129, 0.3)"
      },
      blue: {
        gradient: "from-blue-500 to-cyan-400",
        text: "text-blue-400",
        bg: "bg-blue-500/20",
        border: "border-blue-500/30",
        glow: "rgba(59, 130, 246, 0.3)"
      },
      violet: {
        gradient: "from-violet-500 to-purple-400",
        text: "text-violet-400",
        bg: "bg-violet-500/20",
        border: "border-violet-500/30",
        glow: "rgba(139, 92, 246, 0.3)"
      },
      gold: {
        gradient: "from-gold-500 to-amber-400",
        text: "text-gold-400",
        bg: "bg-gold-500/20",
        border: "border-gold-500/30",
        glow: "rgba(212, 175, 55, 0.3)"
      },
      cyan: {
        gradient: "from-cyan-500 to-teal-400",
        text: "text-cyan-400",
        bg: "bg-cyan-500/20",
        border: "border-cyan-500/30",
        glow: "rgba(6, 182, 212, 0.3)"
      }
    };
    return colors[config.color] || colors.gold;
  };

  const colorClasses = getColorClasses();

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/95 backdrop-blur-md"
    >
      <div className="max-w-lg mx-auto px-6 text-center">
        {/* Léon avec halo animé */}
        <div className="relative mb-8 flex items-center justify-center">
          {/* Halo lumineux pulsant */}
          <motion.div
            className="absolute"
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.2, 0.4, 0.2],
            }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          >
            <div 
              className="w-56 h-56 rounded-full blur-3xl"
              style={{ background: `radial-gradient(circle, ${colorClasses.glow} 0%, transparent 70%)` }}
            />
          </motion.div>

          {/* Avatar Léon */}
          <motion.div
            className="relative z-10"
            animate={{ y: [0, -10, 0] }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
          >
            <motion.div
              className="rounded-full"
              animate={{
                boxShadow: [
                  `0 0 30px ${colorClasses.glow}`,
                  `0 0 60px ${colorClasses.glow}`,
                  `0 0 30px ${colorClasses.glow}`,
                ],
              }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            >
              <LeonAnimatedAvatar 
                className="w-40 h-40" 
                isBlinking={isBlinking} 
                animationType={config.animationType}
              />
            </motion.div>
          </motion.div>

          {/* Icône contextuelle tournante */}
          <motion.div
            className="absolute w-64 h-64"
            animate={{ rotate: 360 }}
            transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
          >
            <motion.div
              className="absolute -top-4 left-1/2 -translate-x-1/2"
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
            >
              <div className={`w-12 h-12 rounded-full ${colorClasses.bg} border ${colorClasses.border} flex items-center justify-center backdrop-blur-sm`}>
                <Icon className={`w-6 h-6 ${colorClasses.text}`} />
              </div>
            </motion.div>
          </motion.div>

          {/* Particules flottantes */}
          {[...Array(6)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-2 h-2 rounded-full bg-gold-500/40"
              initial={{ 
                x: 0, 
                y: 0, 
                opacity: 0 
              }}
              animate={{
                x: [0, Math.cos(i * 60 * Math.PI / 180) * 100],
                y: [0, Math.sin(i * 60 * Math.PI / 180) * 100],
                opacity: [0, 1, 0],
                scale: [0, 1, 0]
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                delay: i * 0.5,
                ease: "easeOut"
              }}
            />
          ))}
        </div>

        {/* Titre principal */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-white mb-4">
            {config.title}
          </h2>
          
          {/* Barre de progression */}
          <div className="w-72 h-2 mx-auto bg-zinc-800 rounded-full overflow-hidden mb-6">
            <motion.div
              className={`h-full bg-gradient-to-r ${colorClasses.gradient}`}
              initial={{ width: "0%" }}
              animate={{ width: `${Math.min(progress, 95)}%` }}
              transition={{ duration: 0.5, ease: "easeOut" }}
            />
          </div>
        </motion.div>

        {/* Messages dynamiques */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className={`${colorClasses.bg} border ${colorClasses.border} rounded-2xl p-6 backdrop-blur-sm`}
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={currentMessageIndex}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
              className="flex items-center justify-center gap-3"
            >
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
              >
                <CheckCircle className={`w-5 h-5 ${colorClasses.text}`} />
              </motion.div>
              <span className={`${colorClasses.text} font-medium text-lg`}>
                {config.messages[currentMessageIndex]}
              </span>
            </motion.div>
          </AnimatePresence>

          {/* Indicateurs de progression des messages */}
          <div className="flex justify-center gap-2 mt-4">
            {config.messages.slice(0, 5).map((_, i) => (
              <motion.div
                key={i}
                className={`w-2 h-2 rounded-full transition-colors duration-300 ${
                  i === (currentMessageIndex % 5) 
                    ? `bg-gradient-to-r ${colorClasses.gradient}` 
                    : 'bg-zinc-700'
                }`}
              />
            ))}
          </div>
        </motion.div>

        {/* Message de patience avec temps écoulé */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="mt-6 space-y-3"
        >
          <p className="text-zinc-500 text-sm">
            Léon travaille pour vous, merci de patienter...
          </p>
          
          {/* Temps écoulé */}
          {elapsedTime > 0 && (
            <p className="text-zinc-600 text-xs">
              Temps écoulé: {formatElapsedTime(elapsedTime)}
            </p>
          )}
          
          {/* Message d'avertissement après 30s */}
          {elapsedTime > 30000 && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-amber-500/70 text-xs"
            >
              L'analyse de documents volumineux peut prendre jusqu'à 2-3 minutes.
            </motion.p>
          )}
          
          {/* Bouton d'annulation */}
          {onCancel && elapsedTime > 5000 && (
            <motion.button
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              onClick={onCancel}
              className="mt-4 px-4 py-2 text-sm text-zinc-400 hover:text-white border border-zinc-700 hover:border-zinc-600 rounded-lg transition-colors"
            >
              Annuler
            </motion.button>
          )}
        </motion.div>
      </div>
    </motion.div>
  );
}

// =====================================================
// CONTEXT ET PROVIDER
// =====================================================
const LeonLoadingContext = createContext(null);

export function LeonLoadingProvider({ children }) {
  const [loadingState, setLoadingState] = useState({
    isLoading: false,
    type: 'generic',
    startTime: null,
    minDisplayTime: 800, // Afficher minimum 800ms pour éviter le flash
    showAfterDelay: 300  // N'afficher que si > 300ms (plus réactif)
  });
  const [showOverlay, setShowOverlay] = useState(false);
  const [timeoutId, setTimeoutId] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [cancelCallback, setCancelCallback] = useState(null);
  
  // Track elapsed time
  useEffect(() => {
    let intervalId;
    if (loadingState.isLoading && loadingState.startTime) {
      intervalId = setInterval(() => {
        setElapsedTime(Date.now() - loadingState.startTime);
      }, 1000);
    } else {
      setElapsedTime(0);
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [loadingState.isLoading, loadingState.startTime]);

  const startLoading = useCallback((type = 'generic', onCancel = null) => {
    const now = Date.now();
    console.log(`[LeonLoading] Starting: type=${type}`);
    
    setLoadingState(prev => ({
      ...prev,
      isLoading: true,
      type,
      startTime: now
    }));
    
    if (onCancel) {
      setCancelCallback(() => onCancel);
    }

    // N'afficher l'overlay que si la requête dépasse le délai
    const id = setTimeout(() => {
      setShowOverlay(true);
    }, loadingState.showAfterDelay);
    
    setTimeoutId(id);
  }, [loadingState.showAfterDelay]);

  const stopLoading = useCallback(() => {
    console.log(`[LeonLoading] Stopping after ${elapsedTime}ms`);
    
    // Annuler le timeout si pas encore affiché
    if (timeoutId) {
      clearTimeout(timeoutId);
      setTimeoutId(null);
    }

    // Si l'overlay est affiché, garder un temps minimum
    if (showOverlay && loadingState.startTime) {
      const elapsed = Date.now() - loadingState.startTime;
      const remaining = Math.max(0, loadingState.minDisplayTime - elapsed);
      
      setTimeout(() => {
        setShowOverlay(false);
        setLoadingState(prev => ({
          ...prev,
          isLoading: false,
          startTime: null
        }));
        setCancelCallback(null);
      }, remaining);
    } else {
      setShowOverlay(false);
      setLoadingState(prev => ({
        ...prev,
        isLoading: false,
        startTime: null
      }));
      setCancelCallback(null);
    }
  }, [showOverlay, loadingState.startTime, loadingState.minDisplayTime, timeoutId, elapsedTime]);

  const handleCancel = useCallback(() => {
    console.log('[LeonLoading] Cancel requested');
    if (cancelCallback) {
      cancelCallback();
    }
    stopLoading();
  }, [cancelCallback, stopLoading]);

  return (
    <LeonLoadingContext.Provider value={{ startLoading, stopLoading, isLoading: loadingState.isLoading }}>
      {children}
      <AnimatePresence>
        {showOverlay && (
          <LeonLoadingOverlay 
            type={loadingState.type} 
            elapsedTime={elapsedTime}
            onCancel={cancelCallback ? handleCancel : null}
          />
        )}
      </AnimatePresence>
    </LeonLoadingContext.Provider>
  );
}

// =====================================================
// HOOKS
// =====================================================
export function useLeonLoading() {
  const context = useContext(LeonLoadingContext);
  if (!context) {
    throw new Error('useLeonLoading must be used within a LeonLoadingProvider');
  }
  return context;
}

// Hook utilitaire pour wrapper les appels API
export function useLeonFetch() {
  const { startLoading, stopLoading } = useLeonLoading();

  const leonFetch = useCallback(async (url, options = {}, loadingType = 'generic') => {
    startLoading(loadingType);
    try {
      const response = await fetch(url, options);
      return response;
    } finally {
      stopLoading();
    }
  }, [startLoading, stopLoading]);

  return leonFetch;
}

// =====================================================
// EXPORTS
// =====================================================
export { LOADING_CONFIGS, LeonLoadingOverlay, LeonAnimatedAvatar };
export default LeonLoadingProvider;
